/**
 * SettingsLayout Component
 *
 * Reusable layout wrapper for all settings sections.
 * Provides consistent structure with title, description, and scrollable content area.
 */

// 1. External dependencies
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PanelLeft, ArrowLeft } from 'lucide-react';

// 2. Workspace packages
import { Card, CardHeader, CardBody, Button, Breadcrumbs, BreadcrumbItem } from '@enfomentis/ui';

/**
 * Configuration for a single breadcrumb item (parent navigation only)
 */
export interface BreadcrumbItemConfig {
  /** Display label for the breadcrumb */
  label: string;
  /** Navigation href - required for all parent items (items without href are skipped) */
  href?: string;
}

interface SettingsLayoutBaseProps {
  /** Section description (omit to hide) */
  description?: string;
  /** Settings content */
  children: React.ReactNode;
  /** Function to open the settings drawer */
  onOpenDrawer?: () => void;
  /** Show back button in header */
  showBackButton?: boolean;
  /** Back button click handler */
  onBack?: () => void;
  /** Back button label */
  backLabel?: string;
  /** Enable scrollable content area (opt-in, default false) */
  scrollableContent?: boolean;
  /** Hide the header entirely (for mobile nested routes with their own headers) */
  hideHeader?: boolean;
  /** Additional actions to display in header (desktop only) */
  headerActions?: React.ReactNode;
  /** Breadcrumb items - on mobile, "Settings" is automatically prepended */
  breadcrumbItems?: BreadcrumbItemConfig[];
  /** Actions to display at the far-right of the breadcrumb row */
  breadcrumbActions?: React.ReactNode;
  /** Additional CSS classes appended to the content area (CardBody) */
  contentClassName?: string;
}

/** When minimalHeader is true, title is optional (breadcrumbs only). Otherwise title is required. */
export type SettingsLayoutProps = SettingsLayoutBaseProps & (
  | { minimalHeader: true; title?: string }
  | { minimalHeader?: false; title: string }
);

/**
 * Reusable Settings Section Layout
 * 
 * Provides consistent Card-based structure for all settings sections:
 * - Card Header with section title and description
 * - Card Body with scrollable content area
 * - Proper mobile behavior and spacing
 */
export const SettingsLayout: React.FC<SettingsLayoutProps> = ({
  title,
  description,
  children,
  onOpenDrawer,
  showBackButton = false,
  onBack,
  backLabel = "Back",
  scrollableContent = false,
  hideHeader = false,
  headerActions,
  breadcrumbItems,
  breadcrumbActions,
  contentClassName,
  minimalHeader = false,
}) => {
  const navigate = useNavigate();

  /**
   * Render breadcrumbs showing parent navigation context only
   * - undefined: No breadcrumbs at all
   * - []: Shows "Settings" only (both mobile and desktop)
   * - [...items]: Shows "Settings → Parent chain" (all items must have href)
   */
  const renderBreadcrumbs = (): React.ReactNode => {
    // undefined = no breadcrumbs at all
    if (breadcrumbItems === undefined) return null;

    // Filter to only items with href (skip invalid items)
    const validItems = breadcrumbItems.filter(
      (item): item is BreadcrumbItemConfig & { href: string } => Boolean(item.href)
    );

    return (
      <Breadcrumbs variant="light" size="sm">
        {/* Always show "Settings" as first breadcrumb */}
        <BreadcrumbItem
          onPress={() => navigate('/settings')}
          className="text-text-secondary hover:text-text-primary"
          isCurrent={false}
        >
          Settings
        </BreadcrumbItem>
        {/* Render parent items (all clickable) */}
        {validItems.map((item, index) => (
          <BreadcrumbItem
            key={`${item.label}-${item.href}-${index}`}
            onPress={() => navigate(item.href)}
            className="text-text-secondary hover:text-text-primary"
            isCurrent={false}
          >
            {item.label}
          </BreadcrumbItem>
        ))}
      </Breadcrumbs>
    );
  };

  return (
    <Card
      radius="none"
      shadow="none"
      className="h-full flex flex-col border-0 relative"
    >
      {!hideHeader && (
        <CardHeader className={`flex-col items-start ${minimalHeader ? 'pb-0' : description ? 'pb-2' : 'pb-0'}`}>
          {/* Breadcrumb row with drawer button (desktop) */}
          {(breadcrumbItems !== undefined || onOpenDrawer || breadcrumbActions) && (
            <div className={`flex items-center gap-2 w-full ${minimalHeader ? 'mb-0' : 'mb-2'}`}>
              {/* Desktop: Expand sidebar button (only when sidebar is collapsed) */}
              {onOpenDrawer && (
                <Button
                  variant="light"
                  size="sm"
                  isIconOnly
                  onPress={onOpenDrawer}
                  title="Open Settings Menu"
                  aria-label="Open Settings Menu"
                  className="hidden md:flex shrink-0"
                >
                  <PanelLeft size={18} />
                </Button>
              )}
              <div className="min-w-0">
                {renderBreadcrumbs()}
              </div>
              {breadcrumbActions && (
                <div className="ml-auto shrink-0">
                  {breadcrumbActions}
                </div>
              )}
            </div>
          )}

          {!minimalHeader && (
            <div className="flex items-start justify-between w-full">
              <div className="flex-1">
                {/* Title */}
                {title && (
                  <h2 className="text-xl md:text-2xl font-bold text-text-primary">
                    {title}
                  </h2>
                )}
                {/* Description below title */}
                {description && (
                  <p className="text-sm md:text-base text-text-secondary mt-1">
                    {description}
                  </p>
                )}
              </div>

              {/* Header actions (desktop only) */}
              {headerActions && (
                <div className="hidden md:flex items-center gap-2 ml-4 shrink-0">
                  {headerActions}
                </div>
              )}

              {/* Back button for sub-sections - positioned in upper right */}
              {showBackButton && onBack && (
                <Button
                  variant="light"
                  size="sm"
                  startContent={<ArrowLeft size={16}/>}
                  onPress={onBack}
                  className="text-text-secondary ml-4 shrink-0 mr-6"
                >
                  {backLabel}
                </Button>
              )}
            </div>
          )}
        </CardHeader>
      )}
      <CardBody className={`flex-1 min-h-0 py-2 ${scrollableContent ? 'overflow-y-auto' : ''}${contentClassName ? ` ${contentClassName}` : ''}`}>
        {children}
      </CardBody>
    </Card>
  );
};